import { useEffect, useState } from "react";
import toast from "../../utils/toast.js";
import { updateCar } from "../../services/carsService.js";

const VEHICLE_TYPE_LABELS = {
  "5-seater": "5-Seater · Sedan",
  "7-seater": "7-Seater · MUV",
};

// Published cars no longer edit seats/routes through the onboarding wizard —
// seats are fixed once published, and routes are managed per-route in the
// My Cars card. This only edits the car's own identity fields.
export default function EditCarDetailsModal({ isOpen, car, onClose, onSaved }) {
  const [carModel, setCarModel] = useState("");
  const [regNo, setRegNo] = useState("");
  const [fuelType, setFuelType] = useState("Petrol");
  const [driverName, setDriverName] = useState("");
  const [driverMobile, setDriverMobile] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && car) {
      setCarModel(car.carModel || "");
      setRegNo(car.registrationNo || "");
      setFuelType(car.fuelType || "Petrol");
      setDriverName(car.driverName || "");
      setDriverMobile(car.driverMobile || "");
      setError("");
    }
  }, [isOpen, car]);

  if (!isOpen || !car) return null;

  async function handleSave(e) {
    e.preventDefault();
    if (
      !carModel.trim() ||
      !regNo.trim() ||
      !driverName.trim() ||
      !/^\d{10}$/.test(driverMobile.trim())
    ) {
      setError("Please fill in all fields (driver mobile must be 10 digits).");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await updateCar(car._id, {
        carType: car.carType,
        carModel: carModel.trim(),
        registrationNo: regNo.trim(),
        fuelType,
        driverName: driverName.trim(),
        driverMobile: driverMobile.trim(),
      });
      if (!res.success) {
        toast.error(res.message);
        return;
      }
      toast.success("Car details updated.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't save car details.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="wizard-overlay open">
      <div className="wizard-modal">
        <button type="button" className="wizard-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <h2>Edit car details</h2>
        <p className="wizard-sub">
          {VEHICLE_TYPE_LABELS[car.carType] || car.carType} — seats and routes are managed
          separately.
        </p>

        <form onSubmit={handleSave}>
          <div className="field">
            <label htmlFor="editCarModel">Car model name</label>
            <input
              type="text"
              id="editCarModel"
              value={carModel}
              onChange={(e) => setCarModel(e.target.value)}
            />
          </div>
          <div className="field-row-2">
            <div className="field">
              <label htmlFor="editRegNo">Registration no.</label>
              <input
                type="text"
                id="editRegNo"
                value={regNo}
                onChange={(e) => setRegNo(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="editFuelType">Fuel</label>
              <select
                id="editFuelType"
                value={fuelType}
                onChange={(e) => setFuelType(e.target.value)}
              >
                <option>Petrol</option>
                <option>Diesel</option>
                <option>CNG</option>
                <option>EV</option>
              </select>
            </div>
          </div>
          <div className="field-row-2">
            <div className="field">
              <label htmlFor="editDriverName">Driver name</label>
              <input
                type="text"
                id="editDriverName"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="editDriverMobile">Driver mobile</label>
              <input
                type="tel"
                id="editDriverMobile"
                inputMode="numeric"
                maxLength={10}
                value={driverMobile}
                onChange={(e) => setDriverMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              />
            </div>
          </div>
          <p className="field-error">{error}</p>

          <div className="wizard-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
